// @ts-strict-ignore
import * as React from 'react';
import { RootState } from '../../reducers/reducers';
import { useSelector } from 'react-redux';
import Text from 'antd/lib/typography/Text';
import {
    selectAppPlatform,
    selectAppSettingAutomations,
    selectAppTimeFormatSetting,
    selectAuthCredentials,
    selectEffectiveTimezone,
    selectIsDemoInstance,
    selectRelease,
} from '../../selectors/selectors';
import { CheckCircleFilled, ClockCircleOutlined, RetweetOutlined } from '@ant-design/icons';
import { allLifecycleEventsForPlatform } from '../automations/automationHelperComponents';
import { antGrey, runwayColors } from '../styles/globalStyles';
import {
    Automation,
    AutomationAutoRelease,
    AutomationAutoSubmitRelease,
    AutomationPromoteStablePhasedRelease,
    AutomationType,
    CompletedReleaseSummary,
    LifecycleEventType,
    LifecycleStep,
    NextReleaseSummary,
    ReleaseCalendarEventType,
    ReleaseCalendarLifecycleEvent,
    Release,
    ReleaseType,
    UpcomingReleaseSummary,
} from '../../types';
import Divider from 'antd/lib/divider';
import { FeatureSection } from '../../typescriptHelpers';
import { EmptyStateWrapper } from '../releaseEmptyState/EmptyStateWrapper';
import { useReleaseLifecycleStepDisplayInfo } from '@/utils/hooks/helpers';
import TimeWithConditionalTz from '@/components/common/TimeWithConditionalTz';
import dayjs from 'dayjs';
import * as API from '../../api';
import { DatetimeFormat, formatDateWithTimezone, formatDelayMinutes } from '@/timeHelpers';
import { ReleaseDelayTooltip } from '@/components/sharedComponents/ReleaseDelayDescription';
import { automationReleaseDelayMinutes } from '@/helpers/automationHelpers';

interface TargetDatesSectionProps {
    orgId: string;
    appId: string;
    releaseId: string;
}

const TargetDatesSection: React.FC<TargetDatesSectionProps> = (props: TargetDatesSectionProps) => {
    const { orgId, appId, releaseId } = props;

    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const release = useSelector((state: RootState) => selectRelease(state, releaseId));
    const platformLifecycleSteps = allLifecycleEventsForPlatform(appPlatform);
    const lifecycleSteps =
        release?.targetReleaseDate && !platformLifecycleSteps.includes(LifecycleStep.Release) ?
            [...platformLifecycleSteps, LifecycleStep.Release]
        :   platformLifecycleSteps;

    const automations = useSelector((state: RootState) => selectAppSettingAutomations(state, appId));
    const autoPromoteAutomation = automations.find((a) => a.type === AutomationType.AutoPromoteStablePhasedReleases) as
        | AutomationPromoteStablePhasedRelease
        | undefined;
    const showFullRolloutBlock = !!autoPromoteAutomation?.enabled;

    return (
        <EmptyStateWrapper
            appId={appId}
            featureSections={[FeatureSection.OverviewTopOverview]}
            releaseId={releaseId}
            customWrapperStyle={{
                minHeight: 'unset',
                padding: '12px 24px',
                margin: '24px 0',
                marginRight: 'auto',
            }}
        >
            {(isInEmptyState) => (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        padding: isInEmptyState ? 20 : '10px 0px 24px 0px',
                    }}
                >
                    {lifecycleSteps.map((step, index) => {
                        return (
                            <div key={index} style={{ display: 'flex', minHeight: '100px' }}>
                                {index ?
                                    <Divider type="vertical" style={{ height: '100%', margin: '0px 20px' }} />
                                :   ''}
                                <DateBlock
                                    step={step}
                                    releaseId={releaseId}
                                    appId={appId}
                                    delay={releaseDelayForStep(step, release, automations)}
                                />
                            </div>
                        );
                    })}
                    {showFullRolloutBlock && <FullRolloutDateBlock orgId={orgId} appId={appId} releaseId={releaseId} />}
                </div>
            )}
        </EmptyStateWrapper>
    );
};

interface DateBlockReleaseDelay {
    until: dayjs.Dayjs;
    minutes: number;
    hasStarted: boolean;
    requestedBy?: string;
}

const releaseDelayForStep = (
    step: LifecycleStep,
    release: Release | undefined,
    automations: Automation[],
): DateBlockReleaseDelay | null => {
    if (step === LifecycleStep.Kickoff || !release) {
        return null;
    }
    const isSubmission = step === LifecycleStep.Submission;
    const startedDelay = release.releaseDelay;
    if (startedDelay) {
        return startedDelay.isSubmission === isSubmission ?
                {
                    until: dayjs(startedDelay.delayedUntil),
                    minutes: startedDelay.delayMinutes,
                    hasStarted: true,
                    requestedBy: startedDelay.requestedBy,
                }
            :   null;
    }
    if (release.type === ReleaseType.Hotfix || release.type === ReleaseType.Rollback) {
        return null;
    }

    const automationType = isSubmission ? AutomationType.AutoSubmitRelease : AutomationType.AutoRelease;
    const minutes =
        automations
            .filter((a) => a.type === automationType)
            .map((a) =>
                automationReleaseDelayMinutes(release.type, a as AutomationAutoRelease | AutomationAutoSubmitRelease),
            )
            .find((m) => m > 0) ?? 0;
    const targetDate = isSubmission ? release.targetSubmitDate : release.targetReleaseDate;
    if (!minutes || !targetDate) {
        return null;
    }
    return { until: dayjs(targetDate).add(minutes, 'minute'), minutes, hasStarted: false };
};

const FullRolloutDateBlock: React.FC<{ orgId: string; appId: string; releaseId: string }> = ({
    orgId,
    appId,
    releaseId,
}) => {
    const authCreds = useSelector((state: RootState) => selectAuthCredentials(state));
    const isDemoInstance = useSelector((state: RootState) => selectIsDemoInstance(state, orgId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, appId));

    const [rolloutEvent, setRolloutEvent] = React.useState<ReleaseCalendarLifecycleEvent | null>(null);

    React.useEffect(() => {
        if (isDemoInstance || !authCreds) {
            return () => {};
        }
        let cancelled = false;
        API.getReleaseCalendar({ orgId, appId, releaseId }, authCreds)
            .then((response) => {
                if (cancelled) return;
                const releaseData = Object.values(response.releaseCalendar ?? {})[0];
                const event = releaseData?.stepLifecycleEvents?.find(
                    (e) => e.lifecycleEventType === LifecycleEventType.LifecycleEventTypeRolloutCompletion,
                );
                setRolloutEvent(event ?? null);
            })
            .catch(() => {
                if (cancelled) return;
                setRolloutEvent(null);
            });
        return () => {
            cancelled = true;
        };
    }, [orgId, appId, releaseId, authCreds, isDemoInstance]);

    const isComplete = rolloutEvent?.dateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate;
    const isPredicted = rolloutEvent?.dateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeEstimatedDate;
    const eventDate = rolloutEvent?.startDate ? dayjs(rolloutEvent.startDate) : null;
    const hasDate = !!eventDate?.isValid();

    if (!rolloutEvent || !hasDate) {
        return null;
    }

    const headerText = isComplete ? 'Full Rollout' : 'Target Full Rollout Date';
    const displayTime = formatDateWithTimezone(
        eventDate,
        'shortHourOnlyWithoutTimezone',
        effectiveTimezone,
        use24HourTimeFormat,
    );
    const displayDate = eventDate.tz(effectiveTimezone).format(DatetimeFormat.DayMonthDate);

    return (
        <div style={{ display: 'flex', minHeight: '100px' }}>
            <Divider type="vertical" style={{ height: '100%', margin: '0px 20px' }} />
            <div style={{ minWidth: '180px' }}>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        paddingBottom: '6px',
                        gap: '10px',
                    }}
                >
                    {isPredicted && <ClockCircleOutlined style={{ fontSize: 14, color: antGrey.grey6 }} />}
                    <Text style={{ fontSize: '14px', color: antGrey.grey6 }}>{headerText}</Text>
                    {isComplete && <CheckCircleFilled style={{ fontSize: 16, color: runwayColors.statusGreen }} />}
                </div>
                <div>
                    {effectiveTimezone ?
                        <TimeWithConditionalTz
                            triggeredAtLabel={displayTime}
                            triggeredByMoment={eventDate}
                            effectiveTimezone={effectiveTimezone}
                            use24HourTimeFormat={use24HourTimeFormat}
                            style={{ fontSize: 18, color: antGrey.grey8 }}
                        />
                    :   <Text style={{ fontSize: 18, color: antGrey.grey8 }}>{displayTime}</Text>}
                </div>
                <div>
                    <Text style={{ fontSize: '18px', color: antGrey.grey8, fontWeight: 600 }}>{displayDate}</Text>
                </div>
            </div>
        </div>
    );
};

export const DateBlock = ({
    step,
    releaseId,
    appId,
    releaseSummary,
    delay,
}: {
    step: LifecycleStep;
    releaseId: string;
    appId: string;
    releaseSummary?: NextReleaseSummary | UpcomingReleaseSummary | CompletedReleaseSummary;
    delay?: DateBlockReleaseDelay | null;
}) => {
    const {
        automationIcon,
        showCadenceIcon,
        isComplete,
        automationEnabled,
        targetDate,
        displayText,
        hasDate,
        displayTime,
        displayDate,
        displayTimeMoment,
    } = useReleaseLifecycleStepDisplayInfo(
        step,
        releaseId,
        appId,
        false,
        releaseSummary,
        'shortHourOnlyWithoutTimezone',
        delay?.until,
    );

    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, appId));

    return (
        <div style={{ minWidth: '180px' }}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingBottom: '6px',
                    gap: '10px',
                }}
            >
                {automationIcon}
                {showCadenceIcon && (
                    <RetweetOutlined
                        style={{
                            fontSize: 14,
                            color: antGrey.grey6,
                        }}
                    />
                )}
                <Text style={{ fontSize: '14px', color: antGrey.grey6 }}>
                    {!isComplete && (automationEnabled || targetDate.isValid()) ?
                        'Target ' + displayText + ' Date'
                    :   displayText}
                </Text>
                {isComplete && <CheckCircleFilled style={{ fontSize: 16, color: runwayColors.statusGreen }} />}
            </div>
            <div>
                {displayTimeMoment && effectiveTimezone ?
                    <TimeWithConditionalTz
                        triggeredAtLabel={displayTime}
                        triggeredByMoment={displayTimeMoment}
                        effectiveTimezone={effectiveTimezone}
                        use24HourTimeFormat={use24HourTimeFormat}
                        style={{ fontSize: 18, color: antGrey.grey8 }}
                    />
                :   <Text
                        style={{
                            fontSize: 18,
                            color: !hasDate ? antGrey.grey6 : antGrey.grey8,
                        }}
                    >
                        {displayTime}
                    </Text>
                }
            </div>
            <div>
                <Text
                    style={{
                        fontSize: '18px',
                        color: `${!hasDate ? antGrey.grey6 : antGrey.grey8}`,
                        fontWeight: 600,
                    }}
                >
                    {displayDate}
                </Text>
            </div>
            {delay && !isComplete && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    <ReleaseDelayTooltip
                        delayMinutes={delay.minutes}
                        isSubmission={step === LifecycleStep.Submission}
                        hasStarted={delay.hasStarted}
                        requestedBy={delay.requestedBy}
                    >
                        {delay.hasStarted ?
                            `Delayed by ${formatDelayMinutes(delay.minutes)}`
                        :   `Planned delay: ${formatDelayMinutes(delay.minutes)}`}
                    </ReleaseDelayTooltip>
                </Text>
            )}
        </div>
    );
};

export default TargetDatesSection;
